import React, { useState } from "react";
import {
  Badge,
  BadgeTone,
  Box,
  Button,
  Flex,
  Label,
  Stack,
  Text
} from "@sanity/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSanityAuthToken } from "../commons/getSanityAuthToken";
import { HttpError } from "../commons/useSanityImportApi";

const POLL_INTERVAL_MS = 2000;

type RunStatus = "neverRun" | "running" | "succeeded" | "failed" | "unknown";

type RunState = {
  status: RunStatus;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
};

type Props = {
  label: string;
  url: string;
};

const badgeFromStatus = new Map<
  RunStatus | undefined,
  { tone: BadgeTone; label: string }
>([
  ["neverRun", { tone: "default", label: "NEVER RUN" }],
  ["running", { tone: "caution", label: "RUNNING" }],
  ["succeeded", { tone: "positive", label: "SUCCEEDED" }],
  ["failed", { tone: "critical", label: "FAILED" }],
  ["unknown", { tone: "critical", label: "UNKNOWN" }],
  [undefined, { tone: "default", label: "LOADING…" }]
]);

const isAuthError = (error: unknown) =>
  error instanceof HttpError && [400, 401, 403].includes(error.status);

const request = async (url: string, method: "GET" | "POST") => {
  const token = getSanityAuthToken();

  if (!token) {
    throw new HttpError(
      401,
      "Authentication required. Please log in to Sanity Studio."
    );
  }

  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new HttpError(
      response.status,
      `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return response.json().catch(() => undefined);
};

const AlgoliaExporterControl: React.FC<Props> = ({ url, label }) => {
  const queryClient = useQueryClient();
  const queryKey = ["algolia-exporter", url];
  const [pending, setPending] = useState<{ previousStartedAt?: string }>();

  const {
    data: run,
    isError: isStatusError,
    error: statusError
  } = useQuery<RunState, Error>({
    queryKey,
    queryFn: () => request(url, "GET"),
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => !isAuthError(error) && failureCount < 3,
    refetchInterval: (query) => {
      const error = query.state.error;
      // An auth error will not fix itself: stop hammering the endpoint.
      return isAuthError(error) ? false : POLL_INTERVAL_MS;
    }
  });

  const {
    mutate: reindex,
    isPending: isTriggering,
    error: triggerError
  } = useMutation<unknown, Error>({
    mutationFn: () => request(url, "POST"),
    // Remember which run was the latest when we fired, so we can show
    // "running" until the API reports a new one.
    onMutate: () => setPending({ previousStartedAt: run?.startedAt }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: () => setPending(undefined)
  });

  // Still waiting for the API to register the run we just triggered.
  const isPending = !!pending && run?.startedAt === pending.previousStartedAt;

  const displayedRun: RunState | undefined = isPending
    ? {
        status: "running",
        startedAt: undefined,
        finishedAt: undefined,
        durationMs: undefined
      }
    : run;

  const badge = badgeFromStatus.get(displayedRun?.status) ?? {
    tone: "default" as BadgeTone,
    label: "LOADING…"
  };
  const errorMessage = (triggerError ?? statusError)?.message;

  return (
    <Box padding={3}>
      <Stack gap={3}>
        <Button
          text={label}
          tone="positive"
          disabled={
            displayedRun?.status === "running" || isTriggering || isStatusError
          }
          onClick={() => reindex()}
        />
        <Flex direction="row" align="center" gap={2}>
          <Label>Status:</Label>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </Flex>
        {displayedRun && (
          <Text size={1} muted>
            {describeRun(displayedRun)}
          </Text>
        )}
        {errorMessage && (
          <Text size={1} muted>
            {errorMessage}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export default AlgoliaExporterControl;

const describeRun = ({
  status,
  startedAt,
  finishedAt,
  durationMs
}: RunState) => {
  if (status === "neverRun") return "No run recorded yet.";
  if (status === "running") {
    return startedAt
      ? `Started: ${formatDate(startedAt)}`
      : "Reindex in progress…";
  }
  if (status === "unknown") {
    return startedAt
      ? `Started: ${formatDate(startedAt)} — no outcome reported`
      : "Last run reported no outcome";
  }

  const parts = [];
  if (startedAt) parts.push(`Start: ${formatDate(startedAt)}`);
  if (finishedAt) parts.push(`Stop: ${formatDate(finishedAt)}`);
  if (typeof durationMs === "number") {
    parts.push(`(${Math.round(durationMs / 1000)}s)`);
  }
  return parts.join(" ");
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    year: "2-digit",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
  });
